import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { recordPageVisit, getResources } from "@/lib/api";
import { 
  Phone, 
  Globe, 
  Clock, 
  MapPin, 
  Search,
  Car,
  Utensils,
  Heart,
  Accessibility,
  DollarSign,
  Shield,
  Pill,
  Home,
  Users,
  ExternalLink,
  Loader2
} from "lucide-react";

const categoryIcons = {
  transportation: Car,
  meals: Utensils,
  counseling: Heart,
  equipment: Accessibility,
  financial: DollarSign,
  veterans: Shield,
  pharmacy: Pill,
  home_health: Home,
  volunteers: Users
};

const categoryColors = {
  transportation: "bg-blue-100 text-blue-700",
  meals: "bg-orange-100 text-orange-700",
  counseling: "bg-pink-100 text-pink-700",
  equipment: "bg-purple-100 text-purple-700",
  financial: "bg-green-100 text-green-700",
  veterans: "bg-indigo-100 text-indigo-700",
  pharmacy: "bg-cyan-100 text-cyan-700",
  home_health: "bg-teal-100 text-teal-700",
  volunteers: "bg-amber-100 text-amber-700"
};

export default function Resources() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    recordPageVisit("resources").catch(console.error);
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      setIsLoading(true);
      const data = await getResources();
      setResources(data.resources || []);
      setCategories(data.categories || {});
    } catch (error) {
      console.error("Failed to load resources:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredResources = resources.filter(resource => {
    const matchesCategory = !selectedCategory || resource.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      resource.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleCall = (phone) => {
    window.location.href = `tel:${phone.replace(/[^0-9]/g, '')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/50 to-background" data-testid="resources-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-teal-50 to-background">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <Badge variant="secondary" className="mb-4">
              <MapPin className="w-3 h-3 mr-1" />
              {t('resources.location', 'Northern Virginia')}
            </Badge>
            <h1 className="heading-1 text-foreground">
              {t('resources.title', 'Community')} <span className="gradient-text">{t('resources.titleHighlight', 'Resources')}</span>
            </h1>
            <p className="body-lg text-muted-foreground">
              {t('resources.description', 'Local support services for hospice families in the Northern Virginia area. Find transportation, meals, counseling, and more.')}
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-md mx-auto mt-8">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                placeholder={t('resources.searchPlaceholder', 'Search resources...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 rounded-full border-primary/20 focus:border-primary"
                data-testid="resource-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="py-6 border-b bg-white">
        <div className="container-max px-6 md:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={selectedCategory === null ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(null)}
              className="rounded-full"
            >
              {t('resources.allResources', 'All Resources')}
            </Button>
            {Object.entries(categories).map(([key, label]) => {
              const Icon = categoryIcons[key] || Heart;
              return (
                <Button
                  key={key}
                  variant={selectedCategory === key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedCategory(key)}
                  className="rounded-full"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  {label}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="section-padding">
        <div className="container-max">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t('resources.noResults', 'No resources found matching your criteria.')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredResources.map((resource) => {
                const Icon = categoryIcons[resource.category] || Heart;
                const colorClass = categoryColors[resource.category] || "bg-gray-100 text-gray-700";
                
                return (
                  <Card key={resource.id} className="glass-card border-0 hover:shadow-lg transition-all" data-testid={`resource-card-${resource.id}`}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className={`w-12 h-12 rounded-xl ${colorClass} flex items-center justify-center`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {categories[resource.category] || resource.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-lg font-serif mt-3">{resource.name}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">{resource.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="w-4 h-4 flex-shrink-0" />
                          <span>{resource.service_area}</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span>{resource.hours}</span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={() => handleCall(resource.phone)}
                          className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                          data-testid={`call-${resource.id}`}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          {resource.phone}
                        </Button>
                        {resource.website && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full"
                            onClick={() => window.open(resource.website, '_blank')}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Help Section */}
      <section className="section-padding bg-primary/5">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-4">
            <h2 className="heading-3 text-foreground">{t('resources.needHelp', 'Need Help Finding Resources?')}</h2>
            <p className="text-muted-foreground">
              {t('resources.helpDescription', 'Our AI assistant can help you find the right community resources based on your specific needs. Just describe what you\'re looking for.')}
            </p>
            <Button 
              className="rounded-full mt-4"
              onClick={() => window.location.href = '/chat?context=community_resources'}
            >
              {t('resources.chatWithAI', 'Chat with AI Assistant')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
